import { useCallback } from 'react';

import {
  ActivationEndpoints,
  ActivationInfo,
  AddCameraRequest,
  AddOrUpdatePresetGroupRequest,
  AddOrUpdatePresetRequest,
  AddOrUpdateWinchRequest,
  CameraEndpoints,
  CameraInfo,
  DeviceProbeInfo,
  PresetEndpoints,
  PresetGroupEndpoints,
  PresetGroupInfo,
  PresetInfo,
  ProbeCameraRequest,
  ProbeEndpoints,
  ProbeWinchRequest,
  SetActivationRequest,
  SetMaintenanceModeRequest,
  UpdateCameraRequest,
  WinchEndpoints,
} from '@scaleaq/sitecontrol-lib';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';

import { API_NAMES, CACHE_KEYS } from '@/constants';
import {
  type ServiceQueryOptions,
  useAuthApiDeleteById,
  useAuthApiGetSingleFetch,
  useAuthApiPost,
  useAuthApiUpdate,
} from '@/dataAccess/rest/apiHooks';

export const useFetchCameras = ({ enabled }: ServiceQueryOptions = {}) => {
  return useAuthApiGetSingleFetch<CameraInfo[]>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: [CACHE_KEYS.allCameras],
    route: CameraEndpoints.getDevices,
    identifier: 'Cameras',
    enabled,
  });
};
export const useRemoveCamera = () => {
  return useAuthApiDeleteById<CameraInfo>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: [CACHE_KEYS.allCameras],
    identifier: 'Camera',
    route: CameraEndpoints.deleteDeviceById,
    returnJsonResult: false,
  });
};

export const useAddCamera = () => {
  return useAuthApiPost<AddCameraRequest>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: [CACHE_KEYS.allCameras],
    identifier: 'Camera',
    route: CameraEndpoints.addDevice,
  });
};

/** Asks the camera at the given address for its serial number. Nothing is saved. */
export const useProbeCamera = () => {
  return useAuthApiPost<ProbeCameraRequest, DeviceProbeInfo>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: [],
    identifier: 'Camera',
    route: ProbeEndpoints.probeCamera,
    returnJsonResult: true,
  });
};

/** Asks the winch at the given address for its serial number. Nothing is saved. */
export const useProbeWinch = () => {
  return useAuthApiPost<ProbeWinchRequest, DeviceProbeInfo>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: [],
    identifier: 'Winch',
    route: ProbeEndpoints.probeWinch,
    returnJsonResult: true,
  });
};

export const useUpdateCamera = (inventoryId: string) => {
  return useAuthApiUpdate<UpdateCameraRequest>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: [CACHE_KEYS.allCameras],
    identifier: 'Camera',
    route: CameraEndpoints.updateDeviceById.replace('{inventoryId}', inventoryId),
    putOrPatch: 'PATCH',
  });
};

/**
 * Puts the camera in or out of maintenance mode (shown as "Standby" in the UI). The camera list
 * is fetched again afterwards, since it carries `maintenanceModeEnabled`.
 */
export const useSetCameraMaintenanceMode = (inventoryId: string) => {
  return useAuthApiUpdate<SetMaintenanceModeRequest, CameraInfo>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: [CACHE_KEYS.allCameras],
    identifier: 'Camera',
    route: CameraEndpoints.setMaintenanceMode.replace('{inventoryId}', inventoryId),
    putOrPatch: 'PUT',
  });
};

export const useUpdateWinch = (inventoryId: string) => {
  return useAuthApiUpdate<AddOrUpdateWinchRequest>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: [CACHE_KEYS.allCameras],
    identifier: 'Winch',
    route: CameraEndpoints.addOrUpdateWinch.replace('{inventoryId}', inventoryId),
    putOrPatch: 'PUT',
  });
};

export const useRemoveWinch = () => {
  return useAuthApiDeleteById<CameraInfo>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: [CACHE_KEYS.allCameras],
    identifier: 'Winch',
    route: CameraEndpoints.removeWinch,
    returnJsonResult: false,
  });
};

export const useGetAllPresets = ({ enabled = true }: ServiceQueryOptions = {}) => {
  return useAuthApiGetSingleFetch<PresetInfo[]>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: [CACHE_KEYS.allPresets],
    route: PresetEndpoints.getAllPresets,
    identifier: 'Preset',
    enabled,
  });
};

/**
 * Presets stored on one inventory level. Skips the request when `inventoryPath` is missing so a
 * camera whose parent is still loading, or that has no parent, does not call
 * `GET /api/v1/preset/level/`.
 */
export const useGetPresetByInventoryPath = ({
  inventoryPath,
  enabled = true,
}: ServiceQueryOptions & { inventoryPath: string | undefined }) => {
  return useAuthApiGetSingleFetch<PresetInfo[]>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: [CACHE_KEYS.allPresets, inventoryPath],
    route: PresetEndpoints.getPresetsByPath.replace('{levelPath}', inventoryPath ?? ''),
    identifier: 'PresetByInventoryPath',
    enabled: enabled && Boolean(inventoryPath),
  });
};

export const useGetPresetGroup = ({ enabled = true }: ServiceQueryOptions) => {
  return useAuthApiGetSingleFetch<PresetGroupInfo[]>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: [CACHE_KEYS.allPresetGroups],
    route: PresetGroupEndpoints.getAllPresetGroups,
    identifier: 'PresetGroup',
    enabled,
  });
};

/**
 * Presets embed `presetGroups` and groups embed `presets`. A write on either
 * side leaves the other list stale, and the next edit resubmits those nested
 * ids, which drops the membership that just changed.
 */
const PRESET_MEMBERSHIP_CACHE_KEYS = [CACHE_KEYS.allPresets, CACHE_KEYS.allPresetGroups];

export const useAddPresetGroup = () => {
  return useAuthApiPost<AddOrUpdatePresetGroupRequest>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: PRESET_MEMBERSHIP_CACHE_KEYS,
    identifier: 'PresetGroup',
    route: PresetGroupEndpoints.addPresetGroup,
  });
};

export const useUpdatePresetGroup = ({ presetGroupId }: { presetGroupId: string }) => {
  return useAuthApiUpdate<AddOrUpdatePresetGroupRequest>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: [...PRESET_MEMBERSHIP_CACHE_KEYS, presetGroupId],
    identifier: 'PresetGroup',
    route: PresetGroupEndpoints.updatePresetGroup.replace('{presetGroupId}', presetGroupId),
    putOrPatch: 'PUT',
  });
};

export const useRemovePresetGroup = () => {
  return useAuthApiDeleteById<PresetGroupInfo>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: PRESET_MEMBERSHIP_CACHE_KEYS,
    identifier: 'PresetGroup',
    // Delete-by-id only substitutes {id}, {inventoryId}, and {ipAddress}.
    route: PresetGroupEndpoints.deletePresetGroup.replace('{presetGroupId}', '{id}'),
    returnJsonResult: false,
  });
};

export const useAddPreset = () => {
  return useAuthApiPost<AddOrUpdatePresetRequest>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: PRESET_MEMBERSHIP_CACHE_KEYS,
    identifier: 'Preset',
    route: PresetEndpoints.addPreset,
  });
};

export const useUpdatePreset = ({ presetId }: { presetId: string }) => {
  return useAuthApiUpdate<AddOrUpdatePresetRequest>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: [...PRESET_MEMBERSHIP_CACHE_KEYS, presetId],
    identifier: 'Preset',
    route: PresetEndpoints.updatePreset.replace('{presetId}', presetId),
    putOrPatch: 'PUT',
  });
};

export const useRemovePreset = () => {
  return useAuthApiDeleteById<PresetInfo>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: PRESET_MEMBERSHIP_CACHE_KEYS,
    identifier: 'Preset',
    // Delete-by-id only substitutes {id}, {inventoryId}, and {ipAddress}.
    route: PresetEndpoints.deletePreset.replace('{presetId}', '{id}'),
    returnJsonResult: false,
  });
};

export const useResetWinchMaintenanceMode = () =>
  useAuthApiPost<{ cameraId: string }>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: [CACHE_KEYS.allCameras],
    identifier: 'Winch',
    route: WinchEndpoints.resetMaintenanceMode,
    resolveRoute: ({ cameraId }) =>
      WinchEndpoints.resetMaintenanceMode.replace('{cameraId}', cameraId ?? ''),
    sendEmptyBody: true,
    returnJsonResult: false,
  });

export const useIsCameraControlActivated = ({ enabled }: ServiceQueryOptions = {}) => {
  return useAuthApiGetSingleFetch<ActivationInfo>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: [CACHE_KEYS.cameraActivation],
    route: ActivationEndpoints.getActivation,
    identifier: 'Activation',
    enabled,
  });
};

export const useSetCameraControlActivation = () => {
  return useAuthApiUpdate<SetActivationRequest>({
    baseApiRoute: API_NAMES.cameracontrolApi,
    cacheKeys: [CACHE_KEYS.cameraActivation],
    identifier: 'Activation',
    route: ActivationEndpoints.setActivation,
    putOrPatch: 'PUT',
  });
};

/** Camera control does not answer immediately after being activated, so retry on this schedule. */
const CAMERA_LIST_RETRY_DELAYS_MS = [2000, 5000, 10000, 20000];

/**
 * Refresh the camera list after camera control was switched on or off.
 *
 * Deactivating writes an empty list rather than refetching. The service keeps answering `GET
 * /camera` for a while after the PUT returns, so any refetch here succeeds and restores the very
 * cameras being cleared; once it does start failing, TanStack Query would keep serving the last
 * successful data anyway. An empty list is the known end state, so write it directly.
 *
 * Activating cannot be predicted the same way, and the service needs a moment before it serves
 * cameras, so reset and retry on a short schedule until they come back.
 *
 * Retry timers live at module scope so collapsing the cameras accordion (or any other unmount of
 * the activation UI) does not cancel remaining polls after an empty first `resetQueries`.
 */
export const useRefreshCameraListAfterActivation = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (activated: boolean) => {
      clearCameraListRetries();

      if (!activated) {
        void queryClient.cancelQueries({ queryKey: [CACHE_KEYS.allCameras] });
        queryClient.setQueriesData<CameraInfo[]>({ queryKey: [CACHE_KEYS.allCameras] }, []);
        return;
      }

      void queryClient.resetQueries({ queryKey: [CACHE_KEYS.allCameras] });

      cameraListRetryTimers = CAMERA_LIST_RETRY_DELAYS_MS.map(delay =>
        setTimeout(() => {
          if (hasCameras(queryClient)) {
            clearCameraListRetries();
            return;
          }
          void queryClient.refetchQueries({ queryKey: [CACHE_KEYS.allCameras] });
        }, delay)
      );
    },
    [queryClient]
  );
};

/**
 * A freshly activated service can answer with an empty list before it has registered its cameras,
 * so only a non-empty list means the retries can stop.
 */
const hasCameras = (queryClient: QueryClient) =>
  queryClient
    .getQueriesData<CameraInfo[]>({ queryKey: [CACHE_KEYS.allCameras] })
    .some(([, cameras]) => (cameras?.length ?? 0) > 0);

let cameraListRetryTimers: ReturnType<typeof setTimeout>[] = [];

const clearCameraListRetries = () => {
  cameraListRetryTimers.forEach(clearTimeout);
  cameraListRetryTimers = [];
};
